import React, { useState } from 'react';
import './App.css';
import ResumeUpload from './components/ResumeUpload';
import JobDescriptionInput from './components/JobDescriptionInput';
import AgentSelector from './components/AgentSelector';
import ResultsDisplay from './components/ResultsDisplay';
import { processResume, comprehensiveAnalysis, downloadReport, downloadAgentPDF } from './services/api';

interface ProcessResults {
  resume_tweaker?: any;
  skill_gap_analysis?: any;
  interview_questions?: any;
}

interface ComprehensiveResults {
  results: ProcessResults;
  summary: string;
  recommendations: string[];
  pdf_report_path?: string;
}

function App() {
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [selectedAgents, setSelectedAgents] = useState({
    resume_tweaker: true,
    skill_gap_analysis: true,
    interview_questions: true
  });
  const [results, setResults] = useState<ProcessResults | null>(null);
  const [comprehensiveResults, setComprehensiveResults] = useState<ComprehensiveResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useComprehensiveAnalysis, setUseComprehensiveAnalysis] = useState(true);

  const handleProcess = async () => {
    if (!resumeText.trim() || !jobDescription.trim()) {
      setError('Please provide both resume text and job description');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setComprehensiveResults(null);

    try {
      const request = {
        resume_text: resumeText,
        job_description: jobDescription,
        include_resume_tweaker: selectedAgents.resume_tweaker,
        include_gap_analysis: selectedAgents.skill_gap_analysis,
        include_interview_questions: selectedAgents.interview_questions
      };

      if (useComprehensiveAnalysis) {
        const response = await comprehensiveAnalysis(request);
        setComprehensiveResults({
          results: response.results,
          summary: response.summary,
          recommendations: response.recommendations,
          pdf_report_path: response.pdf_report_path
        });
      } else {
        const response = await processResume(request);
        setResults(response.results);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while processing your request');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!comprehensiveResults?.pdf_report_path) return;

    try {
      const filename = comprehensiveResults.pdf_report_path.split('/').pop();
      if (!filename) return;

      const blob = await downloadReport(filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to download report');
    }
  };

  const handleDownloadAgentPDF = async (agentType: string) => {
    if (!resumeText.trim() || !jobDescription.trim()) {
      setError('Please provide both resume text and job description to download PDF');
      return;
    }

    try {
      setError(null);
      const blob = await downloadAgentPDF(agentType, resumeText, jobDescription);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Set filename based on agent type
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '');
      let filename = '';
      switch (agentType) {
        case 'resume-tweaker':
          filename = `enhanced_resume_${timestamp}.pdf`;
          break;
        case 'skill-gap-analysis':
          filename = `skill_gap_analysis_${timestamp}.pdf`;
          break;
        case 'interview-questions':
          filename = `interview_questions_${timestamp}.pdf`;
          break;
        default:
          filename = `ai_analysis_${timestamp}.pdf`;
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to download PDF');
    }
  };

  const handleClear = () => {
    setResumeText('');
    setJobDescription('');
    setResults(null);
    setComprehensiveResults(null);
    setError(null);
  };

  return (
    <div className="App">
      <header className="app-header">
        <div className="container">
          <h1>🤖 AI Resume Assistant</h1>
          <p>Improve your resume and prepare for interviews with AI-powered analysis</p>
        </div>
      </header>

      <main className="container">
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <div className="grid grid-2">
          <div className="card">
            <ResumeUpload 
              resumeText={resumeText}
              onResumeChange={setResumeText}
              onUploadSuccess={(text) => {
                setResumeText(text);
                setError(null);
              }}
              onUploadError={setError}
            />
          </div>
          
          <div className="card">
            <JobDescriptionInput 
              jobDescription={jobDescription}
              onJobDescriptionChange={setJobDescription}
            />
          </div>
        </div>

        <div className="card">
          <AgentSelector 
            selectedAgents={selectedAgents}
            onAgentsChange={setSelectedAgents}
          />
        </div>

        <div className="card">
          <div style={{ textAlign: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={useComprehensiveAnalysis}
                onChange={(e) => setUseComprehensiveAnalysis(e.target.checked)}
              />
              <strong>Use Comprehensive Analysis (includes PDF report generation)</strong>
            </label>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
              Comprehensive analysis provides additional insights, summary, and recommendations with a downloadable PDF report.
            </p>
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button 
              className="btn" 
              onClick={handleProcess}
              disabled={loading || !resumeText.trim() || !jobDescription.trim()}
            >
              {loading ? '🔄 Processing...' : '🚀 Process Resume'}
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={handleClear}
              disabled={loading}
            >
              🗑️ Clear All
            </button>
          </div>
        </div>

        {loading && (
          <div className="card">
            <div className="loading">
              <div className="spinner"></div>
              <span style={{ marginLeft: '12px' }}>AI is analyzing your resume...</span>
            </div>
          </div>
        )}

        {results && (
          <div className="card">
            <ResultsDisplay 
              results={results} 
              resumeText={resumeText}
              jobDescription={jobDescription}
              onDownloadPDF={handleDownloadAgentPDF}
            />
          </div>
        )}

        {comprehensiveResults && (
          <div className="card">
            <div>
              <h2>📊 Comprehensive Analysis Results</h2>
              
              {comprehensiveResults.summary && (
                <div style={{ marginBottom: '20px' }}>
                  <h3>📋 Executive Summary</h3>
                  <div style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '16px', 
                    borderRadius: '6px',
                    whiteSpace: 'pre-wrap',
                    fontSize: '14px'
                  }}>
                    {comprehensiveResults.summary}
                  </div>
                </div>
              )}

              {comprehensiveResults.recommendations.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h3>💡 Actionable Recommendations</h3>
                  <ul style={{ paddingLeft: '20px' }}>
                    {comprehensiveResults.recommendations.map((rec, index) => (
                      <li key={index} style={{ marginBottom: '8px' }}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              <ResultsDisplay 
                results={comprehensiveResults.results}
                resumeText={resumeText}
                jobDescription={jobDescription}
                onDownloadPDF={handleDownloadAgentPDF}
              />

              {comprehensiveResults.pdf_report_path && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <button 
                    className="btn" 
                    onClick={handleDownloadReport}
                  >
                    📥 Download Complete PDF Report
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <footer className="app-footer">
        <div className="container">
          <p>&copy; 2024 AI Resume Assistant. Built with React, FastAPI, and OpenAI.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
